import type { PropertyIn, PropertyPath } from '@/scopes/common/domain/types'
import type { PriceTableAnalytics } from '@/scopes/prices/domain/types'
import { FilterSelection, FiltersResponse } from '@/domain/prices/filters/types'
import { PricesResponse } from '@/domain/prices/prices/types'
import { PromotedPrice } from '@/domain/prices/promoted-price/types'
import { RoomInfo, RoomSelectorResponse } from '@/domain/prices/rooms/types'
import {
  RelatedInboundRoute,
  RouteInfo,
  type SelfDriveSelectorResponse,
  TransportSelectorMappedResponse,
} from '@/domain/prices/transports/types'
import { IsAccommodationAvailableRawResponseData } from '@/infrastructure/prices/availability/types'
import { setIn } from '@/scopes/common/domain/functions/objectDeepOperations'
import { createStore, StoreApi } from 'zustand'

export const emptyPricesResponse = {
  prices: [],
  pricesByDuration: {},
  pricesByDurationAndDate: {},
  year: '0',
  dates: [],
}

export const emptyRoomsResponse = {
  stock: {},
  roomGroups: [],
  specialAgeProfiles: {},
}

export const emptyTransportResponse = {
  transportType: '',
  outboundRoutes: [],
  inboundRoutes: [],
  tripSummary: [],
  paymentMethod: null,
}
export const emptySelfDriveResponse = {
  transportType: '',
  paymentMethod: null,
}

export const emptyPromotedPriceResponse: PromotedPrice = {
  price: {
    value: 0,
    originalValue: 0,
    discountPercentage: 0,
    supportText: '',
  },
  accommodationId: 0,
  departureDate: {
    date: '',
    formatted: '',
  },
  duration: 0,
  services: [],
  filters: [],
  departureAirport: null,
  transportType: '',
  mealplan: '',
  campaignImage: undefined,
  campaignId: undefined,
  acm: {
    transportType: '',
    departureDateRaw: '',
    durationRaw: 0,
    birthdates: [],
    mealplanCode: '',
    acmUrl: '',

  },
  paymentMethod: null,
  campaignCountDown: undefined,
}

export const emptyIsAccommodationAvailableResponse = {
  isAccommodationAvailable: false,
  isFlightOnly: false,
  transportTypes: [],
}
export interface PricesState {
  filterSelection: FilterSelection
  accoId: string
  contextItemId: string
  bookingGateId: string
  getFilters: {
    loading: boolean
    error: boolean
    isEmpty: boolean
  }
  getPrices: {
    loading: boolean
    error: boolean
  }
  getRooms: {
    loading: boolean
    error: boolean
  }
  getTransport: {
    loading: boolean
    error: boolean
  }
  getPromotedPrice: {
    loading: boolean
    error: boolean
    success: boolean
  }
  isAccommodationAvailable: {
    loading: boolean
    error: boolean
    success: boolean
  }
  filterOptions: Partial<FiltersResponse>
  pricesResponse: PricesResponse
  selectedPriceId: string | null
  roomsResponse: RoomSelectorResponse
  selectedRooms: Record<string, string | null> | null
  selectedRoomInfos: RoomInfo[]
  transportResponse: TransportSelectorMappedResponse
  selectedOutboundRouteId: string | null
  selectedInboundRouteId: string | null
  outboundRoutes: RouteInfo[]
  inboundRoutes: RelatedInboundRoute[]
  selfDriveResponse: SelfDriveSelectorResponse
  createBookingDraft: {
    loading: boolean
    error: boolean
    success: boolean
  }
  analytics: PriceTableAnalytics | null
  promotedPriceResponse: PromotedPrice | null
  isAccommodationAvailableResponse: IsAccommodationAvailableRawResponseData
  promotedPriceId: string
}

type PricesStatePath = PropertyPath<PricesState>

export interface PricesStoreActions {
  setIn: (path: PricesStatePath, value: PropertyIn<PricesState, typeof path>) => void
}

const initialState = {
  filterSelection: {},
  accoId: '',
  contextItemId: '',
  bookingGateId: '',
  getFilters: {
    loading: true,
    error: false,
    isEmpty: false,
  },
  getPrices: {
    loading: false,
    error: false,
  },
  getRooms: {
    loading: false,
    error: false,
  },
  getTransport: {
    loading: false,
    error: false,
  },
  getPromotedPrice: {
    loading: false,
    error: false,
    success: false,
  },
  isAccommodationAvailable: {
    loading: true,
    error: false,
    success: false,
  },
  filterOptions: {},
  pricesResponse: emptyPricesResponse,
  selectedPriceId: null,
  roomsResponse: emptyRoomsResponse,
  selectedRooms: null,
  selectedRoomInfos: [],
  transportResponse: emptyTransportResponse,
  selectedOutboundRouteId: null,
  selectedInboundRouteId: null,
  outboundRoutes: [],
  inboundRoutes: [],
  selfDriveResponse: emptySelfDriveResponse,
  createBookingDraft: {
    loading: false,
    error: false,
    success: false,
  },
  analytics: null,
  promotedPriceResponse: null,
  isAccommodationAvailableResponse: emptyIsAccommodationAvailableResponse,
  promotedPriceId: '',
}

export type PricesStore = PricesState & PricesStoreActions
export type PricesStoreApi = Pick<StoreApi<PricesStore>, 'getState'>

export function createPricesStore(initState: PricesState): StoreApi<PricesStore> {
  return createStore<PricesStore>(set => ({
    ...initState,
    setIn: (path, value) => {
      set(state => setIn(state, path, value) as PricesState)
    },
  }))
}

export const newPricesStore = createPricesStore(initialState)
